import React from 'react'
import { NavigateFn } from '@reach/router'
import {
  Flex,
  Button,
  IconButton,
  Confirmation,
  Toaster,
  PrettyNumber,
  NewGridTable,
  IGridTableColumn,
  GridTableStatusEnum,
} from '@ui'
import { IAssetTag } from '@api'
import { Classes } from '@blueprintjs/core'
import MoreActions from './components/MoreActions'
import { IAssetsHook } from './hooks/useAssets'
import { pluralize } from '@utils'
import { ApolloError } from 'apollo-boost'
import track from '@track'
import Event from '@track/Event'
import { Permissions, PermissionsType, IPermissionsOnAsset } from '@permissions'
import classnames from 'classnames'
import css from './tableView.module.scss'

export default function TableView({
  hook,
  clearFilter,
  navigate,
}: {
  navigate?: NavigateFn
  hook: IAssetsHook
  clearFilter: () => void
}) {
  const {
    items,
    totalAssets,
    reload,
    sort,
    sortBy,
    isLoading,
    nextPage,
    pageInfo,
    isPaging,
    isSorting,
    remove,
    query,
  } = hook

  const columns: IGridTableColumn<IAssetTag>[] = [
    {
      label: 'Asset Name',
      sort: 'name',
      Cell: ({ skeleton, cell }) => {
        return (
          <div
            className={`table__link ${classnames({
              [Classes.SKELETON]: skeleton,
            })}`}
            onClick={() => {
              if (cell) navigate?.(`?assetId=${cell.data.id}`)
              track.assets({ eventName: Event.ROW_CLICK })
            }}
          >
            {cell ? cell?.data.name : 'placeholder'}
          </div>
        )
      },
    },
    {
      label: 'Employees Associated',
      sort: 'employeesCount',
      Cell: ({ cell, skeleton }) => {
        return cell?.data.totals.employees ? (
          <div
            className={classnames({
              [Classes.SKELETON]: skeleton,
            })}
          >
            <PrettyNumber value={cell.data.totals.employees} />
          </div>
        ) : (
          <div
            className={classnames({
              [Classes.SKELETON]: skeleton,
            })}
          >
            -
          </div>
        )
      },
    },
    {
      label: 'Spaces Associated',
      sort: 'spacesCount',
      alignRight: true,
      Cell: ({ cell, skeleton }) => {
        return cell?.data.totals.spaces ? (
          <PrettyNumber value={cell.data.totals.spaces} />
        ) : (
          <div
            className={classnames({
              [Classes.SKELETON]: skeleton,
            })}
          >
            -
          </div>
        )
      },
    },
    {
      label: '',
      alignRight: true,
      Cell: ({ cell, isHighlighted }) => {
        if (!cell || !isHighlighted) return null

        return (
          <Permissions type={PermissionsType.ASSET}>
            {(permissions) => {
              const { canDelete } = permissions as IPermissionsOnAsset
              return canDelete ? (
                <Confirmation
                  cancelable
                  position="top-right"
                  text={
                    <div style={{ maxWidth: 200, whiteSpace: 'normal' }}>
                      {cell.data.totals.employees || cell.data.totals.spaces
                        ? 'Delete this asset (and remove from all associated entities)?'
                        : 'Delete this asset?'}
                    </div>
                  }
                  onConfirm={() => {
                    track.assets({ eventName: Event.DELETE_RECORD_DIALOG })
                    remove(cell.data.id)
                      .then(() => {
                        reload()
                        Toaster.success({
                          message: 'Asset Removed',
                        })
                      })
                      .catch((error) => Toaster.fail({ error }))
                  }}
                >
                  <IconButton minimal className="remove-icon" tooltip="Delete" iconName="times" />
                </Confirmation>
              ) : null
            }}
          </Permissions>
        )
      },
    },
  ]

  const formatAssets = (items: IAssetTag[]) => {
    return items.map((d) => ({
      data: d,
    }))
  }

  const formatedAssets = formatAssets(items)

  return (
    <>
      <Toolbar
        totalAssets={totalAssets}
        isFiltered={!!query}
        resetFilter={clearFilter}
        isLoading={isLoading}
        navigate={navigate}
        assetsHook={hook}
      />
      <NewGridTable<IAssetTag>
        status={
          isLoading
            ? GridTableStatusEnum.LOADING
            : isPaging
            ? GridTableStatusEnum.PAGING
            : isSorting
            ? GridTableStatusEnum.SORTING
            : GridTableStatusEnum.IDLE
        }
        columns={columns}
        minCellWidth="auto"
        data={formatedAssets}
        sort={{
          ...sort,
          onSort: sortBy,
        }}
        page={{
          hasNextPage: pageInfo.hasNextPage,
          endCursor: pageInfo.endCursor,
          onNextPage: nextPage,
        }}
        skeletonSize={10}
        headerTop={52}
      />
    </>
  )
}

export function handleErrors(error: ApolloError) {
  let errorMessage: React.ReactNode[] = []
  if (error && error.graphQLErrors && error.graphQLErrors.length) {
    for (const { message } of error.graphQLErrors) {
      errorMessage = [...errorMessage, <div key={errorMessage.length}>{message}</div>]
    }
  }

  return errorMessage
}

function Toolbar({
  navigate,
  totalAssets,
  assetsHook,
  isFiltered,
  resetFilter,
  isLoading,
}: {
  navigate?: NavigateFn
  assetsHook: IAssetsHook
  totalAssets: number
  isFiltered: boolean
  resetFilter: () => void
  isLoading: boolean
}) {
  return (
    <Flex className={css.assetToolbar}>
      <Flex
        align="center"
        style={{ color: 'var(--grey1)' }}
        className={isLoading ? Classes.SKELETON : ''}
      >
        {totalAssets}{' '}
        {isFiltered ? pluralize(totalAssets, 'filtered result') : pluralize(totalAssets, 'Asset')}
        {isFiltered && (
          <IconButton
            minimal
            style={{ marginLeft: 5 }}
            iconName="times"
            onClick={resetFilter}
            tooltip="Clear Filter"
          />
        )}
      </Flex>
      <Permissions type={PermissionsType.ASSET}>
        {(permissions) => {
          const { canCreate } = permissions as IPermissionsOnAsset
          return canCreate ? (
            <div>
              <Button
                text="New Asset"
                iconName="plus"
                data-button="new_asset"
                onClick={() => {
                  navigate?.('?newItem=assetTag')
                  track.assets({ eventName: Event.NEW_RECORD_DIALOG })
                }}
              />
              <MoreActions assetsHook={assetsHook} />
            </div>
          ) : (
            <div />
          )
        }}
      </Permissions>
    </Flex>
  )
}
